import { useLayoutEffect, useRef } from "react";
import { gsap, ScrollTrigger } from "../utils/gsap";
import TechMarquee from "../components/TechMarquee";
import { assetUrl } from "../utils/paths";

const About = () => {
  const mobileSliderRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRefs = useRef<(HTMLElement | null)[]>([]);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const mediaRefs = useRef<(HTMLElement | null)[]>([]);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  /* ---------------- Mobile slider animation ---------------- */
  useLayoutEffect(() => {
    const track = mobileSliderRef.current;
    if (!track) return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const setup = () => {
      if (tweenRef.current) tweenRef.current.kill();

      const slides = Array.from(track.children).filter(
        (node) => node.tagName === "IMG"
      );
      const sets = Number(track.dataset.sets || 2);
      const setSize = Math.floor(slides.length / sets);
      if (!setSize) return;

      const gap = parseFloat(getComputedStyle(track).gap || "0");
      const setWidth =
        slides
          .slice(0, setSize)
          .reduce((sum, slide) => sum + slide.getBoundingClientRect().width, 0) +
        gap * Math.max(0, setSize - 1);

      if (!setWidth) return;

      const wrapX = gsap.utils.wrap(-setWidth, 0);

      gsap.set(track, { x: 0 });
      tweenRef.current = gsap.to(track, {
        x: -setWidth,
        duration: 18,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: (value) => `${wrapX(parseFloat(value))}px`,
        },
      });
    };

    // Optimization: Defer layout read to avoid forced reflow during hydration
    const rafId = requestAnimationFrame(setup);
    window.addEventListener("resize", setup);
    window.addEventListener("load", setup);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", setup);
      window.removeEventListener("load", setup);
      if (tweenRef.current) tweenRef.current.kill();
    };
  }, []);

  /* ---------------- Scroll animations ---------------- */
  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const ctx = gsap.context(() => {
      const header = headerRefs.current.filter(Boolean);
      const cards = cardRefs.current.filter(Boolean);
      const marquee = marqueeRef.current;
      const media = mediaRefs.current.filter(Boolean);

      gsap.set(header, { autoAlpha: 0, y: 24 });
      gsap.set(cards, {
        autoAlpha: 0,
        y: 48,
        scale: 0.98,
        transformPerspective: 1200,
      });
      if (marquee) gsap.set(marquee, { autoAlpha: 0, y: 32 });

      gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 85%",
          end: "bottom 20%",
        },
      }).to(header, {
        autoAlpha: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.1,
        overwrite: true,
      });

      const getCardOffsetX = (card: Element) =>
        card.closest(".services-column--alt") ? 36 : -36;

      const revealCards = (batch: Element[], direction: string) => {
        const fromY = direction === "down" ? -44 : 44;
        batch.forEach((card, index) => {
          gsap.fromTo(
            card,
            {
              autoAlpha: 0,
              y: fromY,
              x: getCardOffsetX(card),
              rotateX: direction === "down" ? -6 : 6,
              scale: 0.98,
            },
            {
              autoAlpha: 1,
              y: 0,
              x: 0,
              rotateX: 0,
              scale: 1,
              duration: 1,
              ease: "power2.out",
              delay: index * 0.08,
              overwrite: true,
            }
          );
        });
      };

      ScrollTrigger.batch(cards, {
        start: "top 85%",
        end: "bottom 20%",
        onEnter: (batch) => revealCards(batch, "up"),
        onEnterBack: (batch) => revealCards(batch, "down"),
      });

      if (marquee) {
        ScrollTrigger.create({
          trigger: marquee,
          start: "top 90%",
          onEnter: () =>
            gsap.to(marquee, {
              autoAlpha: 1,
              y: 0,
              duration: 0.9,
              ease: "power2.out",
            }),
        });
      }

      media.forEach((item) => {
        gsap.fromTo(
          item,
          { yPercent: 6 },
          {
            yPercent: -6,
            ease: "none",
            scrollTrigger: {
              trigger: item,
              start: "top 90%",
              end: "bottom 10%",
              scrub: 0.6,
            },
          }
        );
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-32 section-spacing services-section section-auto-visibility"
    >
      <div className="services-header">
        <h2
          className="services-title"
          ref={(el) => {
            headerRefs.current[0] = el;
          }}
        >
          How I Can Help Your Business
        </h2>

        <a
          className="services-cta button1"
          href="#tidycall"
          style={{ "--clr": "#7808d0" } as React.CSSProperties}
          ref={(el) => {
            headerRefs.current[1] = el;
          }}
        >
          Get in Touch
          <span className="button1__icon-wrapper" aria-hidden="true">
            <svg
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="button1__icon-svg"
              width="10"
            >
              <path
                d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
                fill="currentColor"
              ></path>
            </svg>

            <svg
              viewBox="0 0 14 15"
              fill="none"
              width="10"
              xmlns="http://www.w3.org/2000/svg"
              className="button1__icon-svg button1__icon-svg--copy"
            >
              <path
                d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
                fill="currentColor"
              ></path>
            </svg>
          </span>
        </a>
      </div>

      <div className="services-grid">
        <div className="services-column">
          <article
            className="service-card service-card--xl"
            ref={(el) => {
              cardRefs.current[0] = el;
            }}
          >
            <h3 className="service-card__title">
              Website Design & Development
            </h3>
            <p className="service-card__text">
              A high-end website that’s fast, SEO-ready, and designed to convert.
              I combine clean UI with solid engineering to deliver premium
              experiences across all devices.
            </p>
            <div
              className="service-card__media"
              ref={(el) => {
                mediaRefs.current[0] = el;
              }}
            >
              <img
                src={assetUrl("projects/keroumi.webp")}
                alt="Website preview"
                className="service-card__img"
                width="3584"
                height="1996"
                loading="lazy"
              />
            </div>
          </article>

          <article
            className="service-card service-card--compact"
            ref={(el) => {
              cardRefs.current[1] = el;
            }}
          >
            <h3 className="service-card__title">Strategic Consultations</h3>
            <p className="service-card__text">
              Clear roadmaps, UX audits, and technical guidance to help you make
              confident decisions and avoid wasted effort.
            </p>
          </article>
        </div>

        <div className="services-column services-column--alt">
          <article
            className="service-card service-card--compact"
            ref={(el) => {
              cardRefs.current[2] = el;
            }}
          >
            <h3 className="service-card__title">UI/UX Design</h3>
            <p className="service-card__text">
              User-first design that feels intuitive and premium. I craft flows
              and interfaces that improve clarity, retention, and conversion.
            </p>
          </article>

          <article
            className="service-card service-card--media"
            ref={(el) => {
              cardRefs.current[3] = el;
            }}
          >
            <h3 className="service-card__title">
              Mobile App Development & AI Automations
            </h3>
            <p className="service-card__text">
              Build polished mobile apps — and automate workflows with AI.
              From iOS & Android apps to smart automations, chatbots, and internal
              tools, I help you scale with less manual work.
            </p>

            <div className="service-card__media-slider">
              <div
                className="service-card__media-track"
                ref={mobileSliderRef}
                data-sets="3"
              >
                <img src={assetUrl("projects/ai-n8n.webp")} alt="" width="1880" height="1178" />
                <img src={assetUrl("projects/mobile-application.webp")} alt="" width="1000" height="1178" />
                <img src={assetUrl("projects/ai-n8n.webp")} alt="" width="1880" height="1178" />
                <img src={assetUrl("projects/mobile-application.webp")} alt="" width="1000" height="1178" />
                <img src={assetUrl("projects/ai-n8n.webp")} alt="" width="1880" height="1178" />
                <img src={assetUrl("projects/mobile-application.webp")} alt="" width="1000" height="1178" />
              </div>
            </div>
          </article>
        </div>
      </div>

      <TechMarquee ref={marqueeRef} />
    </section>
  );
};

export default About;
